import classNames from 'classnames'
import { ComponentType, ReactElement } from 'react'
import type { ButtonProps } from '../../Button'

/**
 * AddActivityButton component
 *
 * A reusable button for adding activities to a contact from the ContactDrawer right content section.
 * This component provides a consistent UI pattern for adding different types of activities such as notes,
 * tasks, follow-ups, calls, emails, etc.
 *
 * The button uses tertiary styling with small size and displays a customizable icon alongside the label.
 *
 * @param props.label - The text label displayed on the button (e.g., "Add Note", "Add Task")
 * @param props.Icon - A React component for the icon to display (receives className prop for styling)
 * @param props.isDisabled - Whether the button should be disabled (prevents clicking when true)
 * @param props.onClick - Callback function triggered when the button is clicked
 *
 * @example
 * <AddActivityButton
 *   label="Add Note"
 *   Icon={NoteIcon}
 *   isDisabled={false}
 *   onClick={() => handleAddNote()}
 * />
 */
type Props = {
  label: string
  mode?: ButtonProps[`mode`]
  Icon: ComponentType<{ className?: string }>
  isDisabled: boolean
  onClick: () => void
  /** Hide the text label and render icon-only (e.g. on mobile). Keeps the label as aria-label. */
  iconOnly?: boolean
}

export default function AddActivityButton({
  label,
  Icon,
  mode = `tertiary`,
  isDisabled,
  onClick,
  iconOnly = false,
}: Props): ReactElement {
  return (
    <button
      type="button"
      aria-label={iconOnly ? label : undefined}
      className={classNames(`btn btn-sm flex items-center gap-2`, {
        'btn-secondary': mode === `secondary`,
        'btn-primary': mode === `primary`,
        'btn-tertiary': mode === `tertiary`,
        'btn-white': mode === `white`,
        'btn-danger': mode === `danger`,
      })}
      onClick={onClick}
      disabled={isDisabled}
    >
      <Icon className="h-4 w-4" />
      {!iconOnly && <span>{label}</span>}
    </button>
  )
}
